import { LocalizedLink as Link } from "@/components/LocalizedLink";
import { Sparkles, Package, ArrowRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useCurrency } from "@/hooks/useCurrency";

interface FreeQuotaBannerProps {
  /** For non-authenticated users who used their free trial */
  variant?: 'signup' | 'upgrade';
}

export const FreeQuotaBanner = ({ variant = 'upgrade' }: FreeQuotaBannerProps) => {
  const { language } = useLanguage();
  const { formatPrice } = useCurrency();

  const tx = (fr: string, en: string, es: string) =>
    language === 'fr' ? fr : language === 'es' ? es : en;

  // Calculate hours until midnight for countdown
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  const hoursLeft = Math.ceil((midnight.getTime() - now.getTime()) / (1000 * 60 * 60));

  if (variant === 'signup') {
    return (
      <div className="mb-6 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 via-background to-primary/10 p-5 backdrop-blur-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-foreground text-sm">
              {tx(
                "Vos 3 analyses gratuites du jour sont utilisées",
                "You've used your 3 free analyses for today",
                "Has usado tus 3 análisis gratuitos de hoy"
              )}
            </h4>
            <p className="text-sm text-muted-foreground mt-0.5">
              {tx(
                "Créez un compte gratuit : 5 crédits offerts pour analyser plusieurs pages ou explorer un site. Ou revenez dans 24 h.",
                "Create a free account: 5 bonus credits to analyze several pages or explore a site. Or come back in 24 hours.",
                "Crea una cuenta gratuita: 5 créditos de regalo para analizar varias páginas o explorar un sitio. O vuelve en 24 h."
              )}
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto">
            <Button size="sm" variant="outline" className="flex-1 sm:flex-none" asChild>
              <Link to="/auth">
                {tx("Créer un compte", "Create account", "Crear cuenta")}
              </Link>
            </Button>
            <Button size="sm" className="flex-1 sm:flex-none gap-1.5" asChild>
              <Link to="/pricing">
                {tx("Voir les offres", "See plans", "Ver planes")}
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 via-background to-primary/10 p-5 backdrop-blur-sm">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center">
          <Clock className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-foreground text-sm">
            {tx(
              "Vos 3 analyses du jour sont utilisées",
              "Your 3 analyses for today are used",
              "Tus 3 análisis de hoy están usados"
            )}
          </h4>
          <p className="text-sm text-muted-foreground mt-0.5">
            {tx(
              `Vos crédits de bienvenue sont épuisés et vos analyses gratuites reviennent dans ~${hoursLeft}h. Passez à un forfait pour analyser sans attendre.`,
              `Your welcome credits are used up and your free analyses come back in ~${hoursLeft}h. Upgrade to a plan to analyze without waiting.`,
              `Tus créditos de bienvenida se agotaron y tus análisis gratuitos vuelven en ~${hoursLeft}h. Mejora tu plan para analizar sin esperar.`
            )}
          </p>
          <div className="flex items-center gap-3 mt-2">
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 rounded-full px-2.5 py-1 border border-border/50">
              <Package className="w-3 h-3" />
              {tx("Dès", "From", "Desde")} {formatPrice(9)}{tx("/mois", "/mo", "/mes")}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 rounded-full px-2.5 py-1 border border-border/50">
              200 {tx("analyses/mois", "analyses/mo", "análisis/mes")}
            </span>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto">
          <Button size="sm" variant="outline" className="flex-1 sm:flex-none gap-1.5" asChild>
            <Link to="/pricing#credits">
              <Package className="w-3.5 h-3.5" />
              {tx("Acheter des crédits", "Buy credits", "Comprar créditos")}
            </Link>
          </Button>
          <Button size="sm" className="flex-1 sm:flex-none gap-1.5" asChild>
            <Link to="/pricing">
              <Sparkles className="w-3.5 h-3.5" />
              {tx("Voir les forfaits", "See plans", "Ver planes")}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};
